import { createApi } from "@reduxjs/toolkit/query/react";
import { supabaseBaseQuery, supabase } from "../../../supabase";
import { serializeError } from "serialize-error";
import type { ModelFamily, ModelProvider, ModelPricing } from "../types";

const modelsApi = createApi({
  reducerPath: "api.Models",
  baseQuery: supabaseBaseQuery,
  endpoints: (build) => ({
    getModels: build.query<ModelFamily[], void>({
      queryFn: async () => {
        const { data, error } = await supabase.from("models").select("*");

        if (error) return { error: serializeError(error) };

        // Define the desired order
        const modelOrder = [
          "gpt-6-astra",
          "gpt-6-sol",
          "gpt-6-luna",
          "gpt-5.6-sol",
          "gpt-5.6-terra",
          "gpt-5.6-luna",
          "gpt-5.5",
          "gpt-5.5-pro",
          "gpt-5.4",
          "gpt-5.4-mini",
          "gpt-5.4-nano",
          "gpt-5.4-pro",
          "gpt-5.3-codex",
          "gpt-5.2",
          "gpt-5.2-chat-latest",
          "gpt-5.2-pro",
          "gpt-5.1",
          "gpt-5",
          "gpt-5-chat-latest",
          "gpt-5-mini",
          "gpt-5-nano",
          "gpt-5-pro",
          "gpt-4.5-preview",
          "gpt-4.1",
          "gpt-4.1-mini",
          "gpt-4.1-nano",
          "gpt-4o",
          "gpt-4o-mini",
          "o1",
          "o1-mini",
          "o1-pro",
          "o3",
          "o3-mini",
          "o3-pro",
          "o4-mini",
          "gpt-4-turbo",
          "gpt-4",
          "gpt-3.5-turbo",
          "claude-fable-5-1",
          "claude-fable-5",
          "claude-opus-5-5",
          "claude-opus-5",
          "claude-opus-4-8",
          "claude-opus-4-7",
          "claude-opus-4-6",
          "claude-opus-4-5",
          "claude-opus-4-1",
          "claude-opus-4",
          "claude-sonnet-5",
          "claude-sonnet-4-6",
          "claude-sonnet-4-5",
          "claude-sonnet-4",
          "claude-haiku-4-5",
          "gemini-3.8-flash",
          "gemini-3.7-flash",
          "gemini-3.6-flash",
          "gemini-3.5-flash",
          "gemini-3.5-flash-lite",
          "gemini-3.1-pro-preview",
          "gemini-3.1-flash-lite",
          "gemini-3-flash-preview",
          "gemini-2.5-pro",
          "gemini-2.5-flash",
          "gemini-2.5-flash-lite",
          "mistral-large-latest",
          "mistral-medium-latest",
          "mistral-small-latest",
          "mistral-small-2506",
          "codestral-latest",
          "magistral-medium-latest",
          "magistral-small-latest",
          "ministral-14b-latest",
          "ministral-8b-latest",
          "ministral-3b-latest",
          "pixtral-large-latest",
          "grok-4.7",
          "grok-4.6",
          "grok-4.5",
          "grok-4.3",
          "grok-build-0.1",
          "grok-4.20-0309-reasoning",
          "grok-4.20-0309-non-reasoning",
          "grok-4-1-fast-reasoning",
          "grok-4-1-fast-non-reasoning",
        ];

        // Sort the data according to the order above
        const sortedModels = (data ?? []).slice().sort((a, b) => {
          const aIndex = modelOrder.indexOf(a.slug);
          const bIndex = modelOrder.indexOf(b.slug);

          // If both are in the list, sort by their order
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex;
          }
          // If only a is in the list, it comes first
          if (aIndex !== -1) return -1;
          // If only b is in the list, it comes first
          if (bIndex !== -1) return 1;
          // Otherwise, sort alphabetically by slug
          return a.slug.localeCompare(b.slug);
        });

        return { data: sortedModels as ModelFamily[] };
      },
    }),

    getModelProviders: build.query<ModelProvider[], void>({
      queryFn: async () => {
        const { data, error } = await supabase
          .from("model_providers")
          .select("*")
          .neq("status", "hidden");

        if (error) return { error: serializeError(error) };

        return { data };
      },
    }),

    getModelPricings: build.query<ModelPricing[], void>({
      queryFn: async () => {
        const { data, error } = await supabase.from("model_pricings").select("*");

        if (error) return { error: serializeError(error) };

        return { data };
      },
    }),
  }),
});

const { useGetModelsQuery, useGetModelProvidersQuery, useGetModelPricingsQuery } = modelsApi;

export { useGetModelsQuery, useGetModelProvidersQuery, useGetModelPricingsQuery, modelsApi };
